/* eslint-disable jsx-a11y/label-has-associated-control */
import { FC, Fragment, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { BackArrow } from 'assets/icons';
import { PaymentMethod } from 'frames-react';
import { CheckoutOtp } from 'views/Accounts/components/Checkout.Com/CheckoutOtp';
import { FlowType } from 'views/Accounts/components/Checkout.Com/CardDetailsForm';
import { classNames } from 'utils';
import { AppContext } from 'context/ContextProvider';

import { useGetCheckoutDotComCards } from 'views/Accounts/hooks/useGetCheckoutDotComCards';
import { cardHasExpired, selectCheckoutDotComCards } from 'views/Accounts/utils';
import { ACCEPTED_PAYMENT_METHODS } from 'views/Accounts/constants';
import { MgmtFeeTypeEnum } from 'views/shared/types';
import MgmtPaymentTypeSelector, { RadioBtnOption } from 'views/shared/MgmtFeePolicyEnforcer/mgmtPaymentTypeSelector';
import { HowToPayEnum } from 'views/shared/HowToPay/type';

enum ChargeAndSaveCardEnum {
  onChargeCardFailure = 'onChargeCardFailure',
  onChargeCardSuccess = 'onChargeCardSuccess',
}

const TRANSACTION_TYPE = 'management fees';

interface ChargeAndSaveCardProps {
  onClose?: () => void;
  show?: boolean;
  amount: number;
  title?: string;
  transactionType?: string;
  onSuccessHash?: string | null;
  onFailureHash?: string | null;
  forceAction?: boolean;
  mgmtFeeType?: MgmtFeeTypeEnum | null;
  onBack?: () => void;
  customHeader?: ReactNode | JSX.Element;
  btnText?: string;
}

const StorageFeePaymentDialog: FC<ChargeAndSaveCardProps> = ({
  title,
  show = false,
  onClose,
  amount,
  transactionType = '',
  onSuccessHash,
  onFailureHash,
  forceAction,
  mgmtFeeType = null,
  onBack,
  customHeader,
  btnText = 'Pay now',
}) => {
  const hasFees = amount > 0;
  const [isOpen, setIsOpen] = useState(show);
  const [selectedOption, setSelectedOption] = useState<RadioBtnOption | null>();
  const [isProcessing, setIsProcessing] = useState(false);

  function closeModal(override = false) {
    if (override || !forceAction) {
      onClose?.();
      setIsOpen(false);
    }
  }

  const onOptionSelected = (selected: RadioBtnOption) => {
    setSelectedOption(selected);
  };

  const {
    state: {
      app: { addCardRef },
      settings: { accountInfo },
    },
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const { data } = useGetCheckoutDotComCards();
  const _cards = selectCheckoutDotComCards(data);

  const additionalPaymentMethods = useMemo((): PaymentMethod[] => {
    if (!mgmtFeeType) return [];
    return mgmtFeeType === MgmtFeeTypeEnum.YEARLY ? [] : [];
  }, [mgmtFeeType]);

  const hasVaildMitCard = useMemo((): boolean => {
    if (!mgmtFeeType) return false;

    const cardsIn = _cards
      .filter((cardScheme) =>
        [...ACCEPTED_PAYMENT_METHODS, ...additionalPaymentMethods].find(
          (x) => x.toLocaleLowerCase() === (cardScheme?.scheme || '').toLocaleLowerCase(),
        ),
      )
      .filter((card) => !cardHasExpired(card))
      .filter((card) => card.mitConsentedFlag);

    return cardsIn.length > 0;
  }, [_cards, additionalPaymentMethods, mgmtFeeType]);

  const options: RadioBtnOption[] = [
    {
      id: HowToPayEnum.CARD_ON_FILE,
      label: 'Card on file',
      value: HowToPayEnum.CARD_ON_FILE,
      description: (
        <label className="mt-4 block cursor-pointer rounded-lg border dark:border-slate-600 p-4 hover:border-gray-400 dark:hover:border-slate-400 w-full">
          <div className="flex items-start gap-3">
            <input
              type="radio"
              checked={selectedOption?.id === HowToPayEnum.CARD_ON_FILE}
              className="mt-1 accent-black dark:accent-teal-500"
              onChange={() =>
                setSelectedOption({
                  id: HowToPayEnum.CARD_ON_FILE,
                  label: 'Card on file',
                  value: HowToPayEnum.CARD_ON_FILE,
                })
              }
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white">Card on file</div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Select from an existing card saved on your account
              </p>
            </div>
          </div>
        </label>
      ),
      data: '',
    },
    {
      id: HowToPayEnum.NEW_CARD,
      label: 'New Card',
      value: HowToPayEnum.NEW_CARD,
      description: (
        <label className="mt-4 block cursor-pointer rounded-lg border dark:border-slate-600 p-4 hover:border-gray-400 dark:hover:border-slate-400 w-full">
          <div className="flex items-start gap-3">
            <input
              type="radio"
              checked={selectedOption?.id === HowToPayEnum.NEW_CARD}
              className="mt-1 accent-black dark:accent-teal-500"
              onChange={() =>
                setSelectedOption({
                  id: HowToPayEnum.NEW_CARD,
                  label: 'New Card',
                  value: HowToPayEnum.NEW_CARD,
                })
              }
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white">New card</div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Pay with a New Card</p>
            </div>
          </div>
        </label>
      ),
      data: '',
    },
  ];

  useEffect(() => {
    if (hasVaildMitCard) {
      setSelectedOption(options[0]);
      return;
    }

    setSelectedOption(options[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVaildMitCard]);

  const addCardRefState = useMemo(() => {
    return encodeURIComponent(btoa(JSON.stringify(addCardRef)));
  }, [addCardRef]);

  // const transaction = useMemo(() => {
  //   switch (transactionType) {
  //     case MgmtViewsEnum.MGMT_FEE_PAYMENT_TYPE:
  //       return 'management fees';
  //     case MgmtViewsEnum.COMBINE_FEE_PAYMENT_TYPE:
  //       return 'management&accruedFees';
  //     default:
  //       return 'accrued fees';
  //   }
  // }, [transactionType]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10 rounded-2xl" onClose={() => null}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto ">
          <div className="flex min-h-full items-center justify-center p-4 text-center rounded-2xl  ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'w-full relative  transform rounded-2xl bg-white dark:bg-slate-800 overflow-hidden   text-left align-middle shadow-xl dark:shadow-slate-900/50 transition-all grid grid-cols-1 ',
                  hasFees && hasVaildMitCard ? 'md:grid-cols-4 max-w-4xl gap-6' : 'md:grid-cols-1 max-w-md',
                  hasFees ? '' : 'pr-6',
                )}
              >
                <button
                  onClick={onClose}
                  className="font-bold text-md absolute top-2 right-4 z-20 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  x
                </button>

                <div className="md:col-span-2 pt-6 pb-6 pl-6  pr-6 md:pr-0 bg-white dark:bg-slate-800">
                  <Dialog.Title
                    as="h3"
                    className="text-lg  flex-col font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    <div className="text-20 font-medium leading-6 text-gray-900 dark:text-white  space-x-3 flex items-center ">
                      {onBack && (
                        <p
                          className="cursor-pointer text-14 z-10  top-4 bg-[#f3f7fa] dark:bg-slate-700 py-1 px-3 rounded-md"
                          onClick={() => {
                            onBack();
                          }}
                        >
                          Back
                        </p>
                      )}
                      <div className="text-left">{title}</div>
                    </div>
                    {/* <span className="text-left text-sm border-none"> {`Amount ${gbpToTargetCurrency.convertAndFormat(amount)}`}</span> */}
                  </Dialog.Title>

                  {hasFees && (
                    <>
                      {hasVaildMitCard && (
                        <>
                          <div className="mt-6">
                            <h3 className="text-[14px] font-medium text-gray-900 dark:text-white">
                              {hasVaildMitCard ? 'Choose a payment method' : 'Pay with card'}
                            </h3>
                          </div>
                          <div>
                            {/* <p className="font-semibold text-14 mb-1"> Payment method</p> */}
                            {options.map((x) => (
                              <>{x.description}</>
                            ))}
                          </div>
                        </>
                      )}

                      {hasVaildMitCard && (
                        <div className="mt-6 rounded-lg border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 p-4 text-sm text-gray-900 dark:text-white">
                          <span className="font-medium text-[14px]">What happens next </span>

                          <ul className="list-disc px-2">
                            <li className="mt-1">
                              {`You're selecting  ${mgmtFeeType === MgmtFeeTypeEnum.YEARLY ? 'Annually' : 'Monthly'}`}
                            </li>
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {!hasFees && (
                    <div className=" md:overflow-y-auto md:overflow-x-hidden">
                      <div className="pb-6">
                        <CheckoutOtp
                          additionalPaymentMethods={additionalPaymentMethods}
                          isUseExistingCard={selectedOption?.id === (HowToPayEnum.CARD_ON_FILE as string)}
                          amount={amount}
                          transactionType={TRANSACTION_TYPE}
                          portfolioId={-1}
                          btnText={btnText}
                          flowType={FlowType.CHARGE_ADD_CARD}
                          onSuccessUrl={
                            onSuccessHash ?? `#${ChargeAndSaveCardEnum.onChargeCardSuccess}=${addCardRefState}`
                          }
                          onFailureUrl={
                            onFailureHash ?? `#${ChargeAndSaveCardEnum.onChargeCardFailure}=${addCardRefState}`
                          }
                          cardsOnFileConfig={
                            mgmtFeeType === MgmtFeeTypeEnum.MONTHLY
                              ? { showOnlyMitEnabled: true }
                              : { showOnlyMitEnabled: false }
                          }
                          isProcessingPayment={setIsProcessing}
                        />

                        {!isProcessing && (
                          <div className="px-[16px]">
                            <button
                              onClick={onClose}
                              className="w-full rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {hasFees && (
                  <div className="md:col-span-2 border-l dark:border-slate-700 bg-[#fbfcfd] dark:bg-slate-700 h-full flex flex-col ">
                    <div className="rounded-lg p-5 ">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Total due today</div>
                      <div className="mt-1 text-[24px] font-semibold text-gray-900 dark:text-white">
                        {gbpToTargetCurrency.convertAndFormat(amount)}
                      </div>
                    </div>
                    <div className=" md:overflow-y-auto md:overflow-x-hidden">
                      <div className="pb-6">
                        <CheckoutOtp
                          additionalPaymentMethods={additionalPaymentMethods}
                          isUseExistingCard={selectedOption?.id === (HowToPayEnum.CARD_ON_FILE as string)}
                          amount={amount}
                          transactionType={TRANSACTION_TYPE}
                          portfolioId={-1}
                          btnText={btnText}
                          flowType={FlowType.CHARGE_ADD_CARD}
                          onSuccessUrl={
                            onSuccessHash ?? `#${ChargeAndSaveCardEnum.onChargeCardSuccess}=${addCardRefState}`
                          }
                          onFailureUrl={
                            onFailureHash ?? `#${ChargeAndSaveCardEnum.onChargeCardFailure}=${addCardRefState}`
                          }
                          cardsOnFileConfig={
                            mgmtFeeType === MgmtFeeTypeEnum.MONTHLY
                              ? { showOnlyMitEnabled: true }
                              : { showOnlyMitEnabled: false }
                          }
                          isProcessingPayment={setIsProcessing}
                        />

                        {!isProcessing && (
                          <div className="px-[16px]">
                            <button
                              onClick={onClose}
                              className="w-full rounded-lg border dark:border-slate-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StorageFeePaymentDialog;
